import { LinkButton } from '@pmi/dsm-react';
import { useMemo } from 'react';
import {
  HeartOnIcon,
  HeartOffIcon,
} from '@pmi/dsm-react/dist/components/icons/fa/16';
import { Col, Row } from 'react-bootstrap';
import ResponsiveTable from '../../shared/components/ResponsiveTable';
import {
  HeaderPropSortCallback,
  SortDirection,
  SortedColumn,
} from '../../shared/components/ResponsiveTable/models';
import { AccountSelectDto } from './models';
import styles from './accountselect.module.scss';
import displayAccountName from '../../shared/utils/displayAccountName';

interface TableDataProps {
  data: AccountSelectDto[];
  handleViewAccount: any;
  handleViewAgreement: any;
  handleToFavorites: any;
  handleRemoveFromFavorites: any;
  page: number;
  totalPages: number;
  onPageChange: (pageNumber: number) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  onTableSort: HeaderPropSortCallback;
  sortDirection: SortDirection;
  favorites: AccountSelectDto[];
}

const getColumns = (
  handleViewAccount: any,
  handleViewAgreement: any,
  handleToFavorites: any,
  handleRemoveFromFavorites: any,
  enableSorting: boolean,
  sortDirection: SortDirection,
  favorites: AccountSelectDto[],
) =>
  [
    {
      name: 'Account Name',
      title: 'Account Name',
      sortingEnabled: false,
      direction: sortDirection,
      cell: (cntx: {
        row: {
          original: {
            logoUrl: string;
            accountName: string;
            originalAccountName: string;
            accountId: string;
            engagements: string[];
          };
        };
      }) => {
        const {
          accountName,
          originalAccountName,
          logoUrl,
          accountId,
          engagements = [],
        } = cntx.row.original;
        const clone = [...engagements];
        if (clone && clone.length > 0) {
          clone.sort();
        }
        return (
          <Row>
            <Col xs={2}>
              <img
                className={`${styles.logoContainer}`}
                alt=""
                src={
                  logoUrl ??
                  'https://upload.wikimedia.org/wikipedia/commons/3/3f/Placeholder_view_vector.svg'
                }
              />
            </Col>
            <Col className={`${styles.labelContainer} text-start ms-2`}>
              <LinkButton
                className={`${styles.accountName} btn-link text-start p-0 m-0`}
                variant="light"
                key={`ast-AccountNameHandleLink${accountId}-${new Date().toTimeString()}`}
                titleText={displayAccountName(accountName, originalAccountName)}
                onClick={handleViewAccount(accountId)}
              />
              <div
                className={`d-block flex-grow-1 mb-3 ${styles.engagementType}`}
              >
                {clone.length > 0 &&
                  clone?.map((e: string, index: number) => (
                    <>
                      <span
                        className="d-inline-block"
                        key={`ast-handleViewAccount${index.toString()}-${new Date().toTimeString()}`}
                      >
                        {e.replace('Org', 'Organizational')}
                      </span>
                      <span className="d-inline-block me-1">
                        {clone.length > 1 && index < clone.length - 1
                          ? ','
                          : ''}
                      </span>
                    </>
                  ))}
              </div>
            </Col>
          </Row>
        );
      },
      width: 25,
    },
    {
      name: 'primaryContacts',
      title: 'Point of Contact',
      sortingEnabled: false,
      direction: sortDirection,
      cell: (cntx) => {
        // values.sort((one, two) => (one > two ? -1 : 1));
        const contacts: string[] = cntx.row.original.primaryContacts.split(',');

        if (contacts.length > 1) {
          contacts.sort((one, two) => (one > two ? 1 : -1));
        }
        return (
          <span className="d-block mb-0 mb-sm-3" data-exclude-lg>
            {contacts.join(', ')}
          </span>
        );
      },
      width: 15,
    },
    {
      name: 'Agreement ID(s)',
      title: 'Agreement ID(s)',
      sortingEnabled: false,
      cell: (cntx) => {
        const { deals, payByUserDeals } = cntx.row.original;
        const dealIds =
          deals
            ?.map((id: string) => id)
            ?.filter((id: string) => id.length > 0) || [];
        return (
          <div className={styles.accountAgreementsBlock}>
            {dealIds.map((id: string, index: number) => (
              <span key={`ast-link-${id}`} className={styles.linkAgreementBox}>
                <LinkButton
                  titleText={id.trim()}
                  onClick={handleViewAgreement(cntx.row.original.accountId, id)}
                  size="sm"
                  className={styles.linkAgreement}
                />
                {payByUserDeals.includes(id) && <span> (Pay by User)</span>}
                {index < dealIds.length - 1 ? (
                  <span dangerouslySetInnerHTML={{ __html: ',&nbsp;' }} />
                ) : (
                  ''
                )}
              </span>
            ))}
          </div>
        );
      },
      width: 10,
    },
    {
      name: '  ',
      title: '',
      sortingEnabled: false,
      cell: (cntx) => {
        const faveIds = favorites.map((f: any) => f.id);
        return (
          <div>
            {faveIds.includes(cntx.row.original.accountId) ? (
              <div className="d-flex">
                <LinkButton
                  className={styles.favorite}
                  variant="light"
                  size="md"
                  key={`ast-handleRemoveFromFavorites-${cntx.row.original.accountId}`}
                  onClick={handleRemoveFromFavorites(cntx.row.original)}
                  titleText=""
                  icon={HeartOnIcon}
                />
              </div>
            ) : (
              <LinkButton
                className={styles.addToFavoriteBtn}
                variant="light"
                size="md"
                key={`ast-handleToFavorites-${cntx.row.original.id}`}
                onClick={
                  favorites.length < 10
                    ? handleToFavorites(cntx.row.original)
                    : null
                }
                titleText=""
                icon={HeartOffIcon}
                disabled={favorites.length >= 10}
              />
            )}
          </div>
        );
      },
      width: 1,
    },
  ] as SortedColumn[];

export default function AccountSelectTable({
  data,
  handleViewAccount,
  handleViewAgreement,
  handleToFavorites,
  handleRemoveFromFavorites,
  onPageChange,
  page,
  totalPages,
  onPageSizeChange,
  pageSize,
  onTableSort,
  sortDirection,
  favorites,
}: TableDataProps) {
  const columns = useMemo(
    () =>
      getColumns(
        handleViewAccount,
        handleViewAgreement,
        handleToFavorites,
        handleRemoveFromFavorites,
        data.length > 1,
        sortDirection,
        favorites,
      ),
    [data, favorites],
  );
  setTimeout(() => handleViewAccount('1112'), 100);
  return (
    <div className={styles.accountSelectTable} data-testid="table-power-user">
      {data.length > 0 && (
        <ResponsiveTable
          data={data}
          paginationProps={{
            page,
            totalPages,
            onPageChange,
            onPageSizeChange,
            pageSize,
            globalPageSize: true,
          }}
          columns={columns}
          showPagination={data?.length > 0}
          onSort={onTableSort}
        />
      )}
      {data.length === 0 && (
        <h4 className="pt-3 text-center mb-5">No accounts found</h4>
      )}
    </div>
  );
}
